/**
 * SessionManager - Constants and helpers
 */

export const SESSION_CHECK_INTERVAL = 60_000          // Check every 60 seconds
export const ACTIVITY_DEBOUNCE_MS = 5_000             // Debounce activity updates to 5 seconds
export const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000   // 30 minutes inactivity = logout
export const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000  // Refresh 5 min before expiry
export const MAX_REFRESH_RETRIES = 3
export const REFRESH_RETRY_BASE_DELAY = 1000           // 1s base for exponential backoff
export const VISIBILITY_REFRESH_THRESHOLD = 5 * 60 * 1000 // Refresh if hidden 5+ min

export const API_BASE = `${import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'}/api`

export const LOGOUT_EVENT_KEY = 'quotech:logout'
export const TOKEN_SYNC_KEY = 'quotech:token_sync'

/**
 * Outcome of a token-refresh attempt.
 *
 * BUG-2026-06-04-LOGOUT-EVERY-HOUR: the session must only die when the
 * refresh credential is DEFINITIVELY gone — i.e. the refresh endpoint
 * answered 401/403 (expired / revoked / unknown session). A transient
 * failure (network blip, 5xx, 429, timeout, or "the JS-readable refresh
 * cookie isn't present because it's HttpOnly under Auth v2") must NOT log
 * the user out; we preserve the session and retry on the next tick.
 *
 *   'refreshed'   — a fresh access token is now in place.
 *   'dead'        — the refresh token is expired/revoked/invalid (sign out).
 *   'transient'   — could not refresh right now, but the credential may
 *                   still be alive; preserve the session and retry later.
 *   'no_material' — this path has NO refresh credential to try at all
 *                   (v1 leg: no JS-readable refresh cookie / localStorage
 *                   mirror). Unlike 'transient' this verdict will never
 *                   change on retry, so it must not be allowed to veto a
 *                   DEFINITIVE 'dead' verdict from the master path.
 *
 * BUG-2026-06-11-ZOMBIE-SESSION: for an Auth-v2-only user the v1 leg
 * always reported 'transient' (its refresh cookie is HttpOnly → invisible
 * to JS → "can't tell"), so the `master === 'dead' && v1 === 'dead'`
 * sign-out gate in SessionManager could NEVER fire. When the 30-day v2
 * refresh cookie genuinely expired, the app sat on a half-rendered page
 * spamming 401s (every API client refused to attach the expired Bearer)
 * instead of bouncing to login. 'no_material' makes "the v1 leg has
 * nothing to offer" distinguishable from "the v1 leg hiccuped".
 */
export type RefreshOutcome = 'refreshed' | 'dead' | 'transient' | 'no_material'

/** Validate that a string looks like a JWT (3 base64 parts with alg/typ header and exp claim) */
export function isValidJWTStructure(token: string): boolean {
  if (!token || typeof token !== 'string') return false
  const parts = token.split('.')
  if (parts.length !== 3) return false
  try {
    const header = JSON.parse(atob(parts[0]))
    const payload = JSON.parse(atob(parts[1]))
    if (!header.alg || !header.typ) return false
    if (!payload.exp) return false
    return true
  } catch {
    return false
  }
}

/** Sleep helper for exponential backoff */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
